import { DRIVERS_HUB_COPY } from "@/constants/drivers/trust-copy"
import type { APIResponse } from "@/types/drivers/api"
import { redactEmails, selectNamedReviews } from "@/lib/reviews/archive"
import { toSchemaDatePublished } from "@/lib/reviews/schema-date"
import { isDisplayedDriverReview } from "@/lib/drivers/reviews"

const BASE_URL =
  process.env.NEXT_PUBLIC_WEBSITE_HOST || "https://citizenshipper.com"

export function getDriverProfileRichSnippets(
  data: APIResponse["data"],
): Record<string, unknown>[] {
  const pageUrl = `${BASE_URL}/drivers/${data.id}`
  const profileImage = data.profilePhoto?.large?.replace("/m/", "") || ""
  const knowsAbout =
    data.offerings?.[0]?.services?.map((s) => s.name).filter(Boolean) || []
  const joinYear = data.joinedAt
    ? new Date(data.joinedAt).getFullYear()
    : undefined

  const snippets: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "Person",
      name: data.displayName,
      url: pageUrl,
      ...(profileImage && { image: profileImage }),
      ...(data.about && { description: data.about }),
      jobTitle: "Professional Transporter",
      memberOf: {
        "@type": "Organization",
        name: "CitizenShipper",
        url: BASE_URL,
      },
      ...(knowsAbout.length && { knowsAbout }),
    },
  ]

  if (data.ratingsCount > 0) {
    const productSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `${data.displayName} - Transportation Service`,
      ...(profileImage && { image: profileImage }),
      description: joinYear
        ? `Independent transportation service provided by ${data.displayName} through the CitizenShipper marketplace since ${joinYear}.`
        : `Independent transportation service provided by ${data.displayName} through the CitizenShipper marketplace.`,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: data.rating.toFixed(1),
        ratingCount: String(data.ratingsCount),
        bestRating: "5",
        worstRating: "1",
      },
    }

    const namedReviews = selectNamedReviews(
      (data.reviews ?? []).filter(isDisplayedDriverReview),
    )

    // Google rejects a Review whose author is missing or empty, so nameless
    // reviews stay visible on the page but are left out of the markup.
    if (namedReviews.length > 0) {
      productSchema.review = namedReviews
        .slice(0, 3)
        .map(({ review, authorName }) => {
          const datePublished = toSchemaDatePublished(review.createdAt)

          return {
            "@type": "Review",
            author: {
              "@type": "Person",
              name: authorName,
            },
            ...(datePublished ? { datePublished } : {}),
            reviewBody:
              typeof review.content === "string"
                ? redactEmails(review.content)
                : "",
            reviewRating: {
              "@type": "Rating",
              ratingValue: String(review.rating),
              bestRating: "5",
            },
          }
        })
    }

    snippets.push(productSchema)
  }

  snippets.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: BASE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: DRIVERS_HUB_COPY.pet.headingNoun,
        item: `${BASE_URL}/drivers`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.displayName,
        item: pageUrl,
      },
    ],
  })

  return snippets
}
